import React from "react";
import { Link } from "react-router-dom";
import { useSignUp } from "./hooks/useSignUp";
import { PhoneInput } from "@/components/form/PhoneInput";
import { validateSignUpForm } from "./validation";
import { InputLoader } from "@/components/ui/InputLoader";
import { assets } from "@/utils/assets";
import { useTranslation } from "react-i18next";
import { paths } from "@/navigation/paths";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";

export const SignUpPage = () => {
  const { t } = useTranslation();
  const wallpapers = assets.loginWallpapers;
  const [wallpaperIndex, setWallpaperIndex] = React.useState(0);
  const currentWallpaper = wallpapers[wallpaperIndex % wallpapers.length];

  React.useEffect(() => {
    if (wallpapers.length <= 1) return;
    const intervalId = setInterval(() => {
      setWallpaperIndex((prev) => (prev + 1) % wallpapers.length);
    }, 8000);
    return () => clearInterval(intervalId);
  }, [wallpapers.length]);

  const {
    formData,
    fieldsErrors,
    onSubmit,
    onChangePhoneField,
    setFieldErrorsHandler,
    clearFieldErrorsHandler,
    isLoading,
    errorMessage,
  } = useSignUp();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearFieldErrorsHandler();
    const errors = validateSignUpForm(formData);

    if (Object.keys(errors).length > 0) {
      setFieldErrorsHandler(errors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <div className="flex min-h-screen flex-col lg:h-full lg:flex-row">
      <div className="flex w-full flex-1 flex-col justify-center items-center bg-white px-6 py-10 sm:px-10 lg:w-1/2 lg:px-8 lg:py-0 relative">
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>
        <div className="mx-auto w-full max-w-md lg:w-1/2">
          <img
            src={assets.logo}
            alt="logo"
            className="mx-auto mb-8 h-20 lg:h-18 lg:mb-19"
          />

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900">
                {t("signUp.title")}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                {t("signUp.subtitle")}
              </p>
            </div>

            <PhoneInput
              label={t("signUp.phoneLabel")}
              name="phone"
              id="phone"
              placeholder={t("signUp.phonePlaceholder")}
              value={formData.phone}
              onChange={(value) => onChangePhoneField(value)}
              error={fieldsErrors.phone}
            />

            {errorMessage && (
              <div className="text-red-500 text-sm">{errorMessage}</div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="mt-8 w-full rounded-full bg-primary px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-primary-300 lg:mt-10 lg:w-60 lg:h-15 lg:rounded-4xl lg:block lg:mx-auto"
            >
              {isLoading ? <InputLoader /> : t("signUp.continue")}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t("signUp.haveAccount")}{" "}
              <Link
                to={paths.LOGIN}
                className="font-semibold text-primary hover:text-primary-700"
              >
                {t("signUp.signIn")}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden items-center justify-center lg:flex lg:w-1/2">
        <div className="relative h-full w-full">
          <img
            key={currentWallpaper}
            src={currentWallpaper}
            alt="signup wallpaper"
            className="h-full w-full object-cover transition-opacity duration-700 ease-in-out"
          />
        </div>
      </div>
    </div>
  );
};
